import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Loader2 } from "lucide-react";

import { RootState } from "../../../../../../app/rootReducer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { createAddressChangeRequest } from "@/api/createAddressChangeRequest";
import { useNotification } from "@/context/NotificationContext";

export function AddressChangeRequestSection() {
  const { showNotification } = useNotification();
  const { userAppSettings } = useSelector(
    (state: RootState) => state.userAppSettings,
  );

  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const openForm = () => {
    setName(
      [userAppSettings?.firstName, userAppSettings?.lastName]
        .filter(Boolean)
        .join(" "),
    );
    setEmail(userAppSettings?.email ?? "");
    setMessage("");
    setExpanded(true);
  };

  const canSubmit =
    !submitting &&
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    message.trim().length > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    createAddressChangeRequest({
      name: name.trim(),
      email: email.trim(),
      message: message.trim(),
    })
      .then(() => {
        showNotification(
          "Request submitted",
          "Our support team will update your address and follow up with you.",
          "success",
        );
        setExpanded(false);
      })
      .finally(() => setSubmitting(false));
  };

  if (!expanded) {
    return (
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Need to update your address?
        </p>
        <Button onClick={openForm} type="button" variant="outline">
          Request Address Change
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-normal text-gray-900 dark:text-white">
          Request an address change
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Tell us what needs to change and our support team will take care of
          it.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="address-change-name">Name</Label>
          <Input
            id="address-change-name"
            maxLength={255}
            onChange={(e) => setName(e.target.value)}
            value={name}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="address-change-email">Email</Label>
          <Input
            id="address-change-email"
            maxLength={255}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            value={email}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="address-change-message">What should change?</Label>
        <Textarea
          id="address-change-message"
          maxLength={2000}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="e.g. Our billing address is now 123 Main St, Springfield…"
          rows={4}
          value={message}
        />
      </div>

      <div className="flex justify-end gap-2">
        <Button
          onClick={() => setExpanded(false)}
          type="button"
          variant="outline"
        >
          Cancel
        </Button>
        <Button disabled={!canSubmit} onClick={handleSubmit} type="button">
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting…
            </>
          ) : (
            "Submit request"
          )}
        </Button>
      </div>
    </div>
  );
}
